/**
 * Google Calendar API service
 * Uses Google Identity Services (OAuth 2.0 token-based) + Calendar REST API v3
 *
 * Required env var: VITE_GOOGLE_CLIENT_ID
 * Required Google Cloud: Calendar API enabled, OAuth consent screen configured
 * Authorized JS origin: http://localhost:5173 (dev) + your production domain
 */

import type { CalendarEvent } from '../types'

const CALENDAR_API = 'https://www.googleapis.com/calendar/v3'
const SCOPES       = 'https://www.googleapis.com/auth/calendar.events'
const TOKEN_KEY    = 'gc_access_token'
const TOKEN_EXP_KEY = 'gc_token_expiry'
const TIMEZONE     = 'Asia/Kolkata'

// ── Google colorId mapping ──────────────────────────────────────
const TYPE_TO_COLOR_ID: Record<CalendarEvent['type'], string> = {
  meeting:  '7',  // Peacock (teal)
  deadline: '11', // Tomato (red)
  review:   '5',  // Banana (yellow)
  call:     '2',  // Sage (green)
  holiday:  '8',  // Graphite (gray)
}

const COLOR_ID_TO_TYPE: Record<string, CalendarEvent['type']> = {
  '7':  'meeting',
  '11': 'deadline',
  '5':  'review',
  '2':  'call',
  '8':  'holiday',
}

// ── Token storage ───────────────────────────────────────────────
export function saveToken(token: string, expiresInSec: number) {
  localStorage.setItem(TOKEN_KEY, token)
  localStorage.setItem(TOKEN_EXP_KEY, String(Date.now() + expiresInSec * 1000))
}

export function loadToken(): string | null {
  const token  = localStorage.getItem(TOKEN_KEY)
  const expiry = localStorage.getItem(TOKEN_EXP_KEY)
  if (!token || !expiry) return null
  if (Date.now() > parseInt(expiry) - 60_000) {
    // Within 60s of expiry — treat as expired
    clearToken()
    return null
  }
  return token
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY)
  localStorage.removeItem(TOKEN_EXP_KEY)
}

// ── OAuth: init + request token ─────────────────────────────────
let tokenClient: any = null

export function initTokenClient(
  clientId: string,
  onSuccess: (token: string) => void,
  onError: (err: string) => void,
) {
  const g = (window as any).google
  if (!g?.accounts?.oauth2) {
    onError('Google Identity Services not loaded. Check your internet connection.')
    return
  }
  tokenClient = g.accounts.oauth2.initTokenClient({
    client_id: clientId,
    scope: SCOPES,
    callback: (resp: any) => {
      if (resp.error) {
        onError(resp.error_description ?? resp.error)
        return
      }
      const token = resp.access_token as string
      const expiresIn = parseInt(resp.expires_in ?? '3600')
      saveToken(token, expiresIn)
      onSuccess(token)
    },
  })
}

export function requestAccessToken() {
  if (!tokenClient) throw new Error('Token client not initialized')
  tokenClient.requestAccessToken({ prompt: '' })
}

export function revokeToken(token: string): Promise<void> {
  return new Promise(resolve => {
    const g = (window as any).google
    if (g?.accounts?.oauth2) {
      g.accounts.oauth2.revoke(token, () => resolve())
    } else {
      resolve()
    }
    clearToken()
  })
}

// ── Helpers ─────────────────────────────────────────────────────
function toGoogleDateTime(date: string, time?: string) {
  if (time) {
    return { dateTime: `${date}T${time}:00`, timeZone: TIMEZONE }
  }
  return { date }
}

function toGoogleEvent(ev: CalendarEvent) {
  const start = toGoogleDateTime(ev.date, ev.time)
  // End = start + 1 hour (or same day if all-day)
  const end = ev.time
    ? (() => {
        const [h, m] = ev.time.split(':').map(Number)
        const endH   = String(h + 1).padStart(2, '0')
        return { dateTime: `${ev.date}T${endH}:${String(m).padStart(2,'0')}:00`, timeZone: TIMEZONE }
      })()
    : { date: ev.date }

  return {
    summary:     ev.title,
    description: [
      ev.description ?? '',
      ev.clientName ? `Client: ${ev.clientName}` : '',
      `Type: ${ev.type}`,
      `Source: Clickbait ERP`,
    ].filter(Boolean).join('\n'),
    start,
    end,
    colorId: TYPE_TO_COLOR_ID[ev.type] ?? '7',
    reminders: {
      useDefault: false,
      overrides: [
        { method: 'popup', minutes: 30 },
        { method: 'popup', minutes: 10 },
        ...(ev.type !== 'holiday' ? [{ method: 'email', minutes: 60 }] : []),
      ],
    },
  }
}

function fromGoogleEvent(gev: any): CalendarEvent {
  const isDateTime = !!gev.start?.dateTime
  const dateStr = isDateTime
    ? gev.start.dateTime.slice(0, 10)
    : gev.start?.date ?? ''
  const timeStr = isDateTime
    ? gev.start.dateTime.slice(11, 16)
    : undefined

  const colorId = gev.colorId ?? ''
  const type: CalendarEvent['type'] = COLOR_ID_TO_TYPE[colorId] ?? 'meeting'

  return {
    id:           'g-' + gev.id,
    googleEventId: gev.id,
    title:        gev.summary ?? '(No title)',
    date:         dateStr,
    time:         timeStr,
    type,
    description:  gev.description ?? undefined,
    source:       'google',
  }
}

// ── API calls ───────────────────────────────────────────────────
async function gcFetch(
  token: string,
  path: string,
  options: RequestInit = {},
): Promise<any> {
  const res = await fetch(`${CALENDAR_API}${path}`, {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...((options.headers as any) ?? {}),
    },
  })
  if (res.status === 204) return null
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.error?.message ?? `Google Calendar API error ${res.status}`)
  }
  return res.json()
}

/** Create an event on Google Calendar, returns the Google event ID */
export async function createGoogleEvent(
  token: string,
  ev: CalendarEvent,
): Promise<string> {
  const body = toGoogleEvent(ev)
  const data = await gcFetch(token, '/calendars/primary/events', {
    method: 'POST',
    body: JSON.stringify(body),
  })
  return data.id as string
}

/** Delete an event from Google Calendar */
export async function deleteGoogleEvent(
  token: string,
  googleEventId: string,
): Promise<void> {
  await gcFetch(token, `/calendars/primary/events/${googleEventId}`, {
    method: 'DELETE',
  })
}

/** Fetch upcoming Google Calendar events (next N days) */
export async function fetchGoogleEvents(
  token: string,
  daysAhead = 60,
): Promise<CalendarEvent[]> {
  const timeMin = new Date().toISOString()
  const timeMax = new Date(Date.now() + daysAhead * 86_400_000).toISOString()
  const params  = new URLSearchParams({
    timeMin, timeMax,
    singleEvents: 'true',
    orderBy:      'startTime',
    maxResults:   '100',
  })
  const data = await gcFetch(token, `/calendars/primary/events?${params}`)
  return (data?.items ?? []).map(fromGoogleEvent)
}
